import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';

type RowsResult = {
  kind: 'rows';
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
};

type ExecResult = {
  kind: 'exec';
  rows_affected: number;
};

const DEFAULT_SQL = `SELECT id, name, url, created_at FROM crawl_sites ORDER BY id DESC LIMIT 20;`;

const DbExplorerPage = () => {
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RowsResult | ExecResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const envKey = import.meta.env.VITE_ADMIN_DB_EXPLORER_KEY as string | undefined;
  const [extraKey, setExtraKey] = useState('');

  const adminKey = envKey?.trim() || extraKey.trim() || undefined;

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const headers: Record<string, string> = {};
      if (adminKey) headers['X-Admin-Db-Key'] = adminKey;
      const { data } = await axios.post<RowsResult | ExecResult>(
        '/api/admin/db/query',
        { sql },
        { headers }
      );
      setResult(data);
      toast.success(
        data.kind === 'rows'
          ? `${data.row_count} row(s)${data.truncated ? ' (truncated)' : ''}`
          : `${data.rows_affected} row(s) affected`
      );
    } catch (e: unknown) {
      const msg = axios.isAxiosError(e)
        ? (e.response?.data as { detail?: string })?.detail || e.message
        : String(e);
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('Query failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Database explorer</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Run a single SQL statement. Allowed: <code className="text-xs">SELECT</code>,{' '}
          <code className="text-xs">WITH</code>, <code className="text-xs">INSERT</code>,{' '}
          <code className="text-xs">UPDATE</code>, <code className="text-xs">DELETE</code>,{' '}
          <code className="text-xs">EXPLAIN</code>. DDL and dangerous keywords are blocked. Selects return at
          most 500 rows.
        </p>
        {!envKey && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            If the API has <code className="bg-muted px-1 rounded">ADMIN_DB_EXPLORER_KEY</code> set, enter the
            same value below or set <code className="bg-muted px-1 rounded">VITE_ADMIN_DB_EXPLORER_KEY</code> at
            build time.
          </p>
        )}
      </div>

      {!envKey && (
        <div>
          <label className="text-sm font-medium">Optional admin key</label>
          <input
            type="password"
            className="mt-1 w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="X-Admin-Db-Key (only if configured on server)"
            value={extraKey}
            onChange={(e) => setExtraKey(e.target.value)}
            autoComplete="off"
          />
        </div>
      )}

      <div>
        <label className="text-sm font-medium">SQL</label>
        <textarea
          className="mt-1 w-full min-h-[180px] rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          spellCheck={false}
        />
      </div>

      <Button onClick={run} disabled={loading}>
        {loading ? 'Running…' : 'Run'}
      </Button>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {result?.kind === 'exec' && (
        <p className="text-sm text-muted-foreground">
          Rows affected: <strong>{result.rows_affected}</strong>
        </p>
      )}

      {result?.kind === 'rows' && result.rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                {result.columns.map((c) => (
                  <th key={c} className="px-2 py-2 text-left font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60">
                  {result.columns.map((c) => (
                    <td key={c} className="max-w-xs truncate px-2 py-1 align-top font-mono text-xs">
                      {row[c] === null || row[c] === undefined
                        ? ''
                        : typeof row[c] === 'object'
                          ? JSON.stringify(row[c])
                          : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result?.kind === 'rows' && result.rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No rows returned.</p>
      )}
    </div>
  );
};

export default DbExplorerPage;
